import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Plus, Target, Edit2, Trash2, Calendar, User, CheckCircle2, Circle, Flag } from "lucide-react";
import { format, parseISO } from "date-fns";

const CATEGORIES = ["speech", "motor", "behavioral", "social", "academic", "daily_living", "other"];

export default function GoalsPage() {
  const confirm = useConfirmation();
  const [goals, setGoals] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [form, setForm] = useState({
    child_id: "",
    title: "",
    description: "",
    category: "general",
    target_date: "",
    milestones: []
  });
  const [newMilestone, setNewMilestone] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, childrenRes] = await Promise.all([
        apiClient.get("/goals"),
        apiClient.get("/children")
      ]);
      setGoals(goalsRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        milestones: form.milestones.map(m => ({ title: m.title, completed: m.completed || false }))
      };
      if (editingGoal) {
        await apiClient.put(`/goals/${editingGoal.id}`, payload);
        toast.success("Goal updated!");
      } else {
        await apiClient.post("/goals", payload);
        toast.success("Goal created!");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete goal?", description: "This goal and its milestones will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/goals/${id}`);
      toast.success("Goal deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleMilestone = async (goal, milestoneId) => {
    const updatedMilestones = goal.milestones.map(m => 
      m.id === milestoneId ? { ...m, completed: !m.completed } : m
    );
    try {
      await apiClient.put(`/goals/${goal.id}`, { milestones: updatedMilestones });
      fetchData();
      toast.success("Progress updated!");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const openEdit = (goal) => {
    setEditingGoal(goal);
    setForm({
      child_id: goal.child_id,
      title: goal.title,
      description: goal.description,
      category: goal.category,
      target_date: goal.target_date,
      milestones: goal.milestones || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      child_id: "",
      title: "",
      description: "",
      category: "general",
      target_date: "",
      milestones: []
    });
    setNewMilestone("");
    setEditingGoal(null);
    setDialogOpen(false);
  };

  const addMilestone = () => {
    if (!newMilestone.trim()) return;
    setForm({
      ...form,
      milestones: [...form.milestones, { id: Date.now().toString(), title: newMilestone.trim(), completed: false }]
    });
    setNewMilestone("");
  };

  const removeMilestone = (id) => {
    setForm({
      ...form,
      milestones: form.milestones.filter(m => m.id !== id)
    });
  };

  const getChildName = (id) => children.find(c => c.id === id)?.name || "Unknown";

  const getCategoryColor = (cat) => {
    const colors = {
      speech: "bg-teal-100 text-teal-700",
      motor: "bg-coral-100 text-coral-500",
      behavioral: "bg-golden-100 text-golden-500",
      social: "bg-info/10 text-info",
      academic: "bg-purple-100 text-purple-600",
      daily_living: "bg-success/10 text-success"
    };
    return colors[cat] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="grid md:grid-cols-2 gap-6">
          {[1,2,3,4].map(i => <div key={i} className="h-48 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="goals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Target className="h-8 w-8" />
            Goals
          </h1>
          <p className="text-muted-foreground mt-1">Track developmental milestones and achievements</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-goal-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Goal
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingGoal ? "Edit Goal" : "Create Goal"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Child</Label>
                <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Goal Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Use 5-word sentences consistently"
                  required
                  className="rounded-xl"
                  data-testid="goal-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe the goal in detail..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c} value={c} className="capitalize">
                          {c.replace("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Date</Label>
                  <Input
                    type="date"
                    value={form.target_date}
                    onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Milestones</Label>
                <div className="flex gap-2">
                  <Input
                    value={newMilestone}
                    onChange={(e) => setNewMilestone(e.target.value)}
                    placeholder="Add a milestone..."
                    className="rounded-xl"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addMilestone())}
                  />
                  <Button type="button" variant="outline" onClick={addMilestone} className="rounded-full">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2 mt-2">
                  {form.milestones.map(m => (
                    <div key={m.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded-xl">
                      <Circle className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-sm">{m.title}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => removeMilestone(m.id)}
                        className="h-6 w-6 rounded-full"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="save-goal-btn">
                  {editingGoal ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Goals Grid */}
      {goals.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <Target className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Goals Yet</h3>
            <p className="text-muted-foreground mb-6">Start tracking your child's developmental goals</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Create Goal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {goals.map((goal, i) => (
            <Card 
              key={goal.id} 
              className={`border-0 shadow-lg rounded-3xl card-hover animate-fadeIn stagger-${(i % 5) + 1}`}
              data-testid={`goal-card-${goal.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Target className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg line-clamp-1">{goal.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="rounded-full text-xs">
                          <User className="h-3 w-3 mr-1" />
                          {getChildName(goal.child_id)}
                        </Badge>
                        <Badge className={`rounded-full text-xs capitalize ${getCategoryColor(goal.category)}`}>
                          {goal.category.replace("_", " ")}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(goal)} className="rounded-full h-9 w-9">
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(goal.id)}
                      className="rounded-full h-9 w-9 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {goal.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{goal.description}</p>
                )}

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium">Progress</span>
                    <span className="text-primary font-bold">{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-3 rounded-full" />
                </div>

                {/* Milestones */}
                {goal.milestones?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Milestones</p>
                    {goal.milestones.slice(0, 3).map(m => (
                      <button
                        key={m.id}
                        onClick={() => toggleMilestone(goal, m.id)}
                        className={`w-full flex items-center gap-2 p-2 rounded-xl transition-all text-left ${
                          m.completed 
                            ? "bg-success/10 text-success" 
                            : "bg-muted/50 hover:bg-muted text-muted-foreground"
                        }`}
                      >
                        {m.completed ? (
                          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span className={`text-sm flex-1 ${m.completed ? "line-through" : ""}`}>
                          {m.title}
                        </span>
                      </button>
                    ))}
                    {goal.milestones.length > 3 && (
                      <p className="text-xs text-muted-foreground text-center">
                        +{goal.milestones.length - 3} more milestones
                      </p>
                    )}
                  </div>
                )}

                {goal.target_date && (
                  <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                    <Flag className="h-4 w-4" />
                    Target: {format(parseISO(goal.target_date), "MMM d, yyyy")}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
